import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import {Box, Card, TextField, Typography} from "@mui/material";
import Button from "../../../base/button/button";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import ValidateMessages from "../../../../../core/models/static/texts/vallidate-messages";
import Form, {MuiRFF, Yup} from '../../../../components/form';
import useAccessLevels from "../../../../hooks/use-access-levels";
import {createUUId} from "../../../../../core/services/utils/utils";
import {OrderItems} from "./order-items";
import ToastService from "../../../../../core/services/toast";
import CustomerAccountingAndSearchesDetails from "../../../app-specific/customer-accounting-and-searches-details";
import CustomerAutocomplete from "../../../text-fields/autocomplete/customer-autocomplete";
import Grid from "@mui/material/Unstable_Grid2";
import axios from "axios";
import classnames from "classnames"
import {ReactComponent as SaveIcon} from "../../../../../assets/images/save-rollback/check.svg";
import {ReactComponent as CancelIcon} from "../../../../../assets/images/save-rollback/cancel.svg";

;

const ImportWithAISteps = {
    prompt: 'prompt',
    review: 'review',
}

export const ImportWithAISection = ({
                                        defaultWarehouse,
                                        warehouses,
                                        getAvailabilityForPartNo,
                                        validatePartNo,
                                        getPriceForCustomerAndPartNo,
                                        onSuccess,
                                        createDefaultItem,
                                        setDialogWidth,
                                        setActions,
                                    }) => {
    const [step, setStep] = useState(ImportWithAISteps.prompt);
    const [data, setData] = useState({orderExtraInformation: {}, customer: undefined, items: []});
    const operatorAccessLevels = useAccessLevels();

    /**
     * Effect to set the dialog width based on the current step
     */
    useEffect(() => {
        setDialogWidth(() => {
            switch (step) {
                case ImportWithAISteps.prompt:
                default:
                    return 'md';
                case ImportWithAISteps.review:
                    return 'lg';
            }
        })
    }, [setDialogWidth, step]);

    /**
     * Cleanup effect to reset the dialog width when the component is unmounted
     */
    useLayoutEffect(() => () => {
        setDialogWidth(undefined);
    }, [setDialogWidth]);

    /**
     * Callback function passed to the AI import section
     * @type {(function(*[],*=): void)}
     */
    const onImportWithAISuccess = useCallback((data) => {
        const items = data?.items
            .map(e => {
                let quantity = operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders ? (e.qty ?? 0) : Math.trunc(e.qty ?? 0);
                if (!operatorAccessLevels.packages.sales.issueCredit) {
                    quantity = Math.max(0, quantity);
                }
                return ({
                    id: createUUId(),
                    warehouseCode: defaultWarehouse?.code,
                    partNumber: e.partNumber?.toUpperCase()?.trim(),
                    qty: quantity,
                    isValid: undefined,
                    price: e.price,
                })
            })
            .filter(e => !!e.partNumber);
        if (items.length < 5) {
            items.push(...Array.from({length: 5 - items.length}).map(() => createDefaultItem()));
        }
        setData({
            orderExtraInformation: {
                customerPo: data?.purchaseOrder ?? "",
            },
            customer: {
                name: data?.customerName,
                isValid: undefined,
            },
            items: items,
        });
        setStep(ImportWithAISteps.review);
    }, [createDefaultItem, defaultWarehouse?.code, operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders, operatorAccessLevels.packages.sales.issueCredit]);


    /**
     * Callback function to confirm the import
     */
    const confirmImport = useCallback(() => {
        // The items will be checked in the on-success, so we just check for customer.
        if (!data.customer?.isValid) {
            return ToastService.error({
                title: "Invalid Customer",
                message: "Please select a valid customer before confirming the import.",
                hideTicketSubmission: true,
            });
        }
        onSuccess(data);
    }, [data, onSuccess]);

    /**
     * Effect to set the dialog actions based on the current step
     */
    useLayoutEffect(() => {
        switch (step) {
            case ImportWithAISteps.review: {
                setActions([
                    <Button
                        key={'back'}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => setStep(ImportWithAISteps.prompt)}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Back
                    </Button>,
                    <Button
                        key={'confirm'}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={confirmImport}
                        borderRadius={'8px 0 8px 0'}
                    >
                        Submit the order
                    </Button>
                ]);
                break;
            }
            default: {
                setActions([]);
                break;
            }
        }
    }, [setActions, step, confirmImport]);

    /**
     * Cleanup effect to reset the dialog actions when the component is unmounted
     */
    useLayoutEffect(() => () => {
        setActions([]);
    }, [setActions]);

    return (
        <>
            {
                step === ImportWithAISteps.prompt &&
                <ImportWithAIPromptSection
                    onSuccess={onImportWithAISuccess}
                />
            }
            {
                step === ImportWithAISteps.review &&
                <ImportWithAIReviewSection
                    back={() => setStep(ImportWithAISteps.prompt)}
                    data={data}
                    setData={setData}
                    warehouses={warehouses}
                    getAvailabilityForPartNo={getAvailabilityForPartNo}
                    validatePartNo={validatePartNo}
                    getPriceForCustomerAndPartNo={getPriceForCustomerAndPartNo}
                    onSuccess={onSuccess}
                    createDefaultItem={createDefaultItem}
                />
            }
        </>
    );

}

const ImportWithAIPromptSection = ({onSuccess}) => {
    const [importing, setImporting] = useState(false);
    const isMounted = useIsMounted();

    const schema = useMemo(() => Yup.object().shape({
        value: Yup.string().nullable().required(ValidateMessages.required).trim(),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * Callback function to import the items with AI
     * @type {(function(): Promise<void>)}
     */
    const importItemsWithAI = useCallback(async (values) => {
        setImporting(true);
        const response = await Api.importPartsWithAI({text: values.value});
        if (!isMounted())
            return;
        setImporting(false);
        if (response?.resultFlag) {
            onSuccess(response?.data);
        }
    }, [isMounted, onSuccess])

    return (
        <>
            <div className={'d-flex flex-column p-3'}>
                <Typography variant={'body2'} className={'mb-3'}>
                    You can import your desired parts by copying any text containing part numbers and quantities.
                </Typography>
                <Form onSubmit={importItemsWithAI} validate={validate} formProps={{className: 'd-flex flex-column p-3'}}>
                    <MuiRFF.TextField
                        size={'small'}
                        fullWidth
                        multiline
                        minRows={15}
                        maxRows={25}
                        variant={'outlined'}
                        label={'Content to be imported'}
                        placeholder={'Paste the content here'}
                        name={'value'}
                        required={required.value}
                        disabled={importing}
                    />
                    <Button
                        variant={'outlined'}
                        color={'primary'}
                        size={'large'}
                        type={'submit'}
                        className={'mt-3'}
                        disabled={importing}
                    >
                        {importing ? "Importing your content..." : "Import"}
                    </Button>
                </Form>
            </div>
        </>
    );
}

const ImportWithAIReviewSection = ({
                                       data,
                                       setData,
                                       warehouses,
                                       getAvailabilityForPartNo,
                                       validatePartNo,
                                       getPriceForCustomerAndPartNo,
                                       createDefaultItem,
                                   }) => {
    /**
     * Sets the items in the state based on the received updater
     * @type {(function(*): void)}
     */
    const setItems = useCallback((updater) => {
        setData(prevState => ({
            ...prevState,
            items: typeof updater === 'function' ? updater(prevState.items) : updater,
        }))
    }, [setData])

    /**
     * Callback function passed to the last element on the items card to add another row when "Tab" key is pressed
     * @type {(function(*=, *=): void)}
     */
    const addNewRow = useCallback((event, isFromButton) => {
        if (!(isFromButton || event.key === "Tab"))
            return;
        setItems(prevState => [...prevState, createDefaultItem()])
    }, [createDefaultItem, setItems])

    /**
     * Removes the item in the list at the given index
     * @type {(function(*): void)}
     */
    const removeRow = useCallback((id) => {
        setItems(prevState => prevState.filter((e) => e.id !== id));
    }, [setItems])

    /**
     * Updates the item in the state based on the received object
     * @type {(function(*, *): void)}
     */
    const updateRow = useCallback((id, item) => {
        setItems(prevState => prevState.map(e => e.id === id ? {...e, ...item} : e))
    }, [setItems])

    /**
     * Sets the order extra information in the state based on the received updater
     * @type {(function(*): void)}
     */
    const setOrderExtraInformation = useCallback((updater) => {
        setData(prevState => ({
            ...prevState,
            orderExtraInformation: typeof updater === 'function' ? updater(prevState.orderExtraInformation) : updater,
        }))
    }, [setData])

    /**
     * Sets the customer in the state based on the received updater
     * @type {(function(*): void)}
     */
    const setCustomer = useCallback((updater) => {
        setData(prevState => ({
            ...prevState,
            customer: typeof updater === 'function' ? updater(prevState.customer) : updater,
        }))
    }, [setData])

    return (
        <>
            <Grid container spacing={2}>
                <Grid xs={12} lg={5} sx={{order: {xs: 1, lg: 2}, paddingBlock: 3, height: 'fit-content', position: 'sticky', top: 0,}}>
                    <Card sx={{
                        display: 'flex', flexDirection: 'column', gap: 2,
                        padding: 2,
                        borderRadius: 2,
                        backgroundColor: 'background.default',
                    }}>
                        <OrderExtraInformationSection
                            info={data.orderExtraInformation}
                            setInfo={setOrderExtraInformation}
                        />
                        <OrderCustomerSection
                            customer={data.customer}
                            setCustomer={setCustomer}
                        />
                    </Card>
                </Grid>
                <Grid xs={12} lg={7} sx={{order: {xs: 2, lg: 1}}}>
                    <Box sx={{marginBlock: 2}}>
                        <OrderItems
                            itemEntities={data.items}
                            customer={data.customer}
                            addNewRow={addNewRow}
                            removeRow={removeRow}
                            updateRow={updateRow}
                            warehouses={warehouses}
                            validatePartNo={validatePartNo}
                            getAvailabilityForPartNo={getAvailabilityForPartNo}
                            getPriceForCustomerAndPartNo={getPriceForCustomerAndPartNo}
                            pricingSectionEnabled
                        />
                    </Box>
                </Grid>
            </Grid>
        </>
    );
}

const OrderExtraInformationSection = ({info, setInfo}) => {

    /**
     * Updates the order extra information in the state based on the received object
     * @type {(function(*, *): void)}
     */
    const updateInfo = useCallback((key, value) => {
        setInfo(prevState => ({
            ...prevState,
            [key]: value,
        }))
    }, [setInfo])

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2}}>
                <TextField
                    fullWidth
                    variant={'outlined'}
                    label={'Customer PO'}
                    placeholder={'Enter Customer PO'}
                    name={'customerPo'}
                    type={'text'}
                    value={info.customerPo}
                    onChange={(e) => updateInfo('customerPo', e.target.value ?? "")}
                />
            </Box>
        </>
    );
}

const OrderCustomerSection = ({customer, setCustomer}) => {
    const [customersToSelectFrom, setCustomersToSelectFrom] = useState([]);
    const [validating, setValidating] = useState(customer?.isValid === undefined);
    const isMounted = useIsMounted();
    const customerName = React.useMemo(() => customer?.name ?? '', [customer?.name]);
    const shouldValidateCustomer = React.useMemo(() => customerName.trim().length > 0 && (customer?.isValid === undefined), [customer, customerName]);

    /**
     * Callback function to validate the customer based on the entered name
     * @type {(function(*): Promise<void>)}
     */
    const validateCustomer = useCallback(async (cancelToken) => {
        setValidating(true);
        const forApi = {
            sorts: [],
            pagination: {currentPage: 1, pageSize: 10},
            keyword: customerName,
        }
        const response = await Api.searchCustomersV2(forApi, cancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setValidating(false);
        }
        if (response?.resultFlag) {
            const foundCustomers = response.data.items.filter(e => e.name.toLowerCase() === customerName.toLowerCase());
            if (foundCustomers.length === 0) {
                setCustomer(prevState => ({
                    ...prevState,
                    isValid: false,
                }));
            } else if (foundCustomers.length === 1) {
                setCustomer({
                    ...foundCustomers[0],
                    isValid: true,
                });
            } else {
                // Multiple customers found with the same name, let the user select
                setCustomer(undefined);
                setCustomersToSelectFrom(foundCustomers);
            }
        }
    }, [customerName, isMounted, setCustomer])

    /**
     * Effect to validate the customer when the shouldValidateCustomer flag is true
     */
    useEffect(() => {
        if (!shouldValidateCustomer)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void validateCustomer(cancelTokenSource.token);
        return () => {
            cancelTokenSource.cancel();
        }
    }, [shouldValidateCustomer, validateCustomer]);

    /**
     * Callback function passed to the customer autocomplete to set the selected customer
     * @type {(function(*, *): void)}
     */
    const onCustomerChanged = useCallback((e, newCustomer) => {
        setCustomer(newCustomer ? {...newCustomer, isValid: true,} : customer);
        setCustomersToSelectFrom([]);
    }, [customer, setCustomer]);

    const inputError = useMemo(() => !!customer && customer.isValid === false, [customer]);

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                <CustomerAutocomplete
                    fullWidth
                    label={'Customer'}
                    value={customer}
                    onChange={onCustomerChanged}
                    options={customersToSelectFrom}
                    textFieldProps={{
                        variant: 'outlined',
                        error: inputError,
                        helperText:
                            <Box component={'span'} sx={{display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'space-between'}}>
                                <Box component={'span'} className={classnames('checkmark-container')}>
                                    {
                                        !validating && (
                                            !inputError
                                                ? <SaveIcon className={classnames(`save`, "mr-1")}/>
                                                : <CancelIcon className={classnames(`cancel`, "mr-1")}/>
                                        )
                                    }
                                    <Typography variant={'caption'}>
                                        {
                                            validating
                                                ? 'Validating customer...'
                                                : !inputError
                                                    ? !!customer && !customersToSelectFrom.length
                                                        ? 'Customer is valid'
                                                        : "Please select a customer"
                                                    : 'Customer not found or invalid'
                                        }
                                    </Typography>
                                </Box>
                                {
                                    !validating && !inputError && !!customer && !customersToSelectFrom.length &&
                                    <Typography variant={'caption'}>
                                        Code: {customer.code ?? "N/A"}
                                    </Typography>
                                }
                            </Box>,
                    }}
                />
                {
                    !customer && customersToSelectFrom.length > 0 &&
                    <Box sx={{display: 'flex', flexDirection: 'column', marginBlockStart: 1, gap: 1}}>
                        <Typography variant={'body2'}>
                            Multiple customers found with the name "{customerName ?? ""}". Please select one:
                        </Typography>
                        {
                            customersToSelectFrom.map(e => (
                                <Card
                                    key={e.id}
                                    variant={'outlined'}
                                    onClick={() => onCustomerChanged(undefined, e)}
                                    sx={{
                                        padding: 2,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        gap: 1,
                                        alignItems: 'center',
                                        transition: 'background-color 0.3s',
                                        '&:hover': {
                                            backgroundColor: theme => `rgba(${theme.palette.primary.mainChannel} / ${theme.palette.action.hoverOpacity})`,
                                        },
                                    }}
                                >
                                    <Typography variant={'body2'}>
                                        {e.name}
                                    </Typography>
                                    <Typography variant={'body2'} color={'primary.main'}>
                                        Code: {e.code ?? "N/A"}
                                    </Typography>
                                    <Typography variant={'body2'} color={'text.secondary'}>
                                        City: {e.city ?? "N/A"}
                                    </Typography>
                                </Card>
                            ))
                        }
                    </Box>
                }
                {
                    !!customer && !!customer.isValid &&
                    <CustomerAccountingAndSearchesDetails customer={customer} showAccounting/>
                }
            </Box>
        </>
    );
}